import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  CircleDot,
  Clock,
  Dumbbell,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Scale,
  Users,
  Wind,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/neck-lift-surgery-mumbai";

const NECK_LIFT_FAQS = [
  {
    question: "What is the difference between a neck lift and a facelift?",
    answer:
      "A facelift addresses the mid and lower face: jowls, nasolabial folds, and facial skin laxity. A neck lift addresses the neck and jawline: loose neck skin, platysmal bands, and submental fat. The two procedures are frequently combined because lower face and neck aging typically occur together. A neck lift alone is appropriate when neck aging is the primary concern and the face does not yet need surgical correction.",
  },
  {
    question: "How long do neck lift results last?",
    answer:
      "Results typically last 8 to 15 years. Aging continues after surgery, but from a significantly reset baseline. Most patients look substantially younger than their age for years after the procedure. Maintenance with non-surgical treatments can extend the interval before further surgical correction is needed.",
  },
  {
    question: "Will a neck lift remove my double chin?",
    answer:
      "Yes, when the double chin is caused by submental fat, skin laxity, or both. Liposuction removes the fat component and skin tightening addresses the laxity. If the double chin is primarily fat without significant laxity, liposuction alone addresses it. Dr. Jain assesses which approach applies during consultation.",
  },
  {
    question: "Is neck lift surgery painful?",
    answer:
      "Surgery is performed under general anesthesia or sedation. Post-operative discomfort involves tightness and mild soreness in the neck and jaw area rather than sharp pain. This is managed with medication and resolves within the first week to ten days.",
  },
  {
    question: "Will neck lift scars be visible?",
    answer:
      "No. Incisions are placed behind the ears within the natural hairline contour and, when needed, in the crease beneath the chin. Both positions are not visible in normal social situations. Scars fade significantly within 6 to 12 months.",
  },
  {
    question: "Can I have a neck lift without a facelift?",
    answer:
      "Yes. A standalone neck lift is appropriate when neck aging is the dominant concern and the face does not require simultaneous correction. Patients whose primary complaint is the neck, bands, or submental fat without significant facial jowling or laxity are good candidates for neck lift alone.",
  },
  {
    question: "When can I return to work after a neck lift?",
    answer:
      "Most patients return to desk work within 2 weeks. Physical activity and strenuous exercise resume after 4 to 6 weeks. Patients whose work involves significant physical exertion require a longer recovery period before returning.",
  },
  {
    question: "Can neck lift be combined with other procedures?",
    answer:
      "Yes. The most common combination is facelift for complete lower face and neck rejuvenation. Chin augmentation is frequently added to enhance jawline definition alongside neck correction. Liposuction of the submental area is often included as part of the neck lift itself rather than as a separate procedure.",
  },
];

const neckLiftStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalProcedure", "SurgicalProcedure"],
      name: "Neck lift surgery (platysmaplasty / lower rhytidectomy)",
      alternateName: ["Platysmaplasty", "Lower rhytidectomy", "Neck lift", "Neck contouring surgery"],
      description:
        "Surgical tightening of loose neck skin, platysma muscle repair (platysmaplasty), and submental fat reduction to restore jawline and neck definition. Distinct from facelift, which primarily addresses mid- and lower-face jowls and facial skin laxity; neck lift and facelift are often combined when both areas need correction.",
      procedureType: "Surgical",
      bodyLocation: {
        "@type": "AnatomicalStructure",
        name: "Neck",
      },
      followup:
        "Week 1: swelling, bruising, compression garment. Week 2: light activity, sutures out days 7–10. Weeks 3–4: return to routine, avoid strenuous exercise. Months 2–3: final contour as swelling resolves; refinement continues to 6 months.",
      howPerformed:
        "General anesthesia or sedation; incisions behind ears and in posterior hairline; submental incision when needed for fat or muscle access; liposuction or direct excision of submental fat; platysmaplasty midline muscle repair; excess skin removed and redraped; fine sutures; compression garment; 2–4 hours.",
      url: PAGE_URL,
      additionalProperty: [
        { "@type": "PropertyValue", name: "Procedure category", value: "Surgical" },
        { "@type": "PropertyValue", name: "Related procedure distinction", value: "Neck and jawline vs. facelift mid/lower face" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: NECK_LIFT_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

function NeckLiftJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(neckLiftStructuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Face", href: "/#services" },
    { name: "Neck Lift", href: "/neck-lift-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Face</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Neck Lift Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Platysmaplasty &amp; Neck Contouring by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Neck Lift" icon="◇" size="hero"  imageSrc={HERO.neckLift} imageAlt="Neck lift"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What is Neck Lift Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A neck lift, medically called platysmaplasty or lower rhytidectomy, tightens loose neck skin, repairs the
              underlying platysma muscle, removes excess fat, and restores definition to the jawline and neck contour. It is
              the most effective surgical treatment for neck aging because it addresses all three structural causes
              simultaneously: skin laxity, muscle separation, and fat accumulation.
            </p>
            <p>
              The neck ages differently from the face. Skin here is thinner, collagen production declines earlier, and the
              platysma muscle, which runs vertically from the jaw to the collarbone, separates along the midline with age
              creating the visible vertical bands that no topical treatment corrects. Fat accumulates in the submental area
              beneath the chin, and gravity pulls loosened skin downward continuously. These changes require surgical
              correction to reverse.
            </p>
            <p>
              Dr. Vicky Jain performs neck lift surgery as a standalone procedure and in combination with facelift
              for patients who want complete lower face and neck rejuvenation. Results last 8 to 15 years and produce a
              jawline and neck contour that is consistently the most undertreated area in facial aging.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Dr. Jain&apos;s Approach to Neck Lift Surgery
            </h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Dr. Jain plans each neck lift based on which of the three structural problems is dominant in that patient. A
                patient whose primary concern is vertical neck bands needs platysma muscle repair as the central step. A
                younger patient with good skin elasticity but submental fat accumulation needs liposuction, not skin removal.
                A patient with significant skin laxity alongside fat and muscle concerns needs all three elements addressed.
                Treating every neck the same way produces inconsistent results.
              </p>
              <p>
                Incision placement determines scar visibility. Dr. Jain positions incisions behind the ears within the natural
                hairline contour and, when a submental incision is needed, within the natural crease beneath the chin where it
                is not visible. The neck is one of the most exposed areas of the body. Scar placement here requires the same
                precision as the surgical technique itself.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61,37,102,0) 20%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatCorrectsSection() {
  const cards = [
    {
      title: "Sagging Neck Skin",
      icon: Layers,
      copy: "Loose skin that drapes below the jawline and down the neck is the hallmark of advanced neck aging. A neck lift removes the excess skin and redrapes the remainder over a tightened underlying structure.",
    },
    {
      title: "Vertical Neck Bands",
      icon: Minus,
      copy: "Platysmal banding occurs when the two edges of the platysma muscle separate along the midline. Platysmaplasty sutures these edges together, eliminating the bands and restoring a smooth neck surface.",
    },
    {
      title: "Double Chin from Skin Laxity",
      icon: Scale,
      copy: "When a double chin is caused by loose skin rather than fat accumulation, liposuction alone does not correct it. Skin removal and muscle tightening are required to restore a clean jaw-to-neck transition.",
    },
    {
      title: "Submental Fat",
      icon: CircleDot,
      copy: "Excess fat beneath the chin blunts the jaw angle and creates a full, undefined transition from face to neck. This fat is removed through liposuction or direct excision as part of the neck lift procedure.",
    },
    {
      title: "Loss of Jawline Definition",
      icon: Wind,
      copy: "A defined jawline requires tissue to sit above the jaw, not below it. Neck lift repositions and tightens the structures responsible for jawline clarity, producing a sharper lower face contour.",
    },
    {
      title: "Aging Mismatch Between Face and Neck",
      icon: Users,
      copy: "Patients who have had facelift surgery or whose face has aged more slowly than their neck often present with a visible mismatch. A standalone neck lift brings the neck into alignment with the face without repeating facial surgery.",
    },
  ];

  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="neck-corrects-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Indications</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What Neck Lift Surgery Corrects</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]" data-testid={`neck-corrects-card-${index}`}>
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PROCEDURE_TYPES = [
  {
    title: "Traditional Neck Lift",
    body:
      "The most comprehensive approach. Incisions run behind the ears and along the posterior hairline, with a small submental incision beneath the chin when needed. Dr. Jain removes excess fat, tightens the platysma muscle through platysmaplasty, removes excess skin, and closes with fine sutures. Best for patients with moderate to significant neck laxity, platysmal banding, and submental fat.",
  },
  {
    title: "Limited Incision Neck Lift",
    body:
      "A shorter-scar approach for patients with mild to moderate sagging who do not yet need full skin removal. Incisions are confined to the area behind the ears. Best for younger patients with early neck laxity and good skin quality.",
  },
  {
    title: "Neck Liposuction",
    body:
      "Fat removal only, through a small incision beneath the chin. No skin removal and no muscle work. Best for patients under 40 with good skin elasticity whose primary concern is submental fat rather than laxity or banding. Not appropriate when skin laxity is present, as removing fat without skin tightening worsens the sagging.",
  },
  {
    title: "Platysmaplasty",
    body:
      "Focuses specifically on repairing the separated platysma muscle to eliminate vertical neck bands. Frequently performed as part of a traditional neck lift but can be done as a standalone procedure in patients whose primary concern is banding without significant skin excess.",
  },
  {
    title: "Neck Lift with Facelift",
    body:
      "The most common combination for patients with simultaneous lower face and neck aging. Addressing both in a single procedure produces a consistent result across the jawline and neck that a neck lift alone cannot achieve when the face also shows significant descent.",
  },
];

function ProcedureTypesSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="neck-procedure-types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white">Types of Neck Lift Procedures</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {PROCEDURE_TYPES.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgeryPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Neck Lift Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 2 to 4 hours under general anesthesia or sedation depending on complexity and whether additional
              procedures are performed simultaneously. Dr. Jain marks incision placement before surgery with the patient
              upright, assessing the natural hairline contour and skin descent to plan the most concealed scar positions.
            </p>
            <p>
              Incisions are made behind the ears and within the posterior hairline. A small submental incision beneath the
              chin is added when fat removal or direct muscle access is required. Through these incisions, excess submental
              fat is removed by liposuction or direct excision, the platysma muscle is sutured along the midline to repair
              banding and tighten the neck foundation, and excess skin is carefully removed. The skin is then redraped without
              tension and closed with fine sutures.
            </p>
            <p>
              A compression garment is applied immediately after surgery and worn continuously for the first week to support
              the healing tissue and reduce swelling. Most patients stay one night or go home the same day depending on the
              extent of surgery.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const VS_NONSURGICAL_ROWS = [
  {
    label: "Skin Tightening",
    neckLift: "Removes excess skin and redrapes over tightened muscle.",
    nonSurgical: "Surface tightening only, no skin removal.",
  },
  {
    label: "Platysmal Bands",
    neckLift: "Repaired surgically through platysmaplasty.",
    nonSurgical: "Botulinum toxin softens bands temporarily, does not repair muscle separation.",
  },
  {
    label: "Fat Removal",
    neckLift: "Liposuction or direct excision as part of surgery.",
    nonSurgical: "Deoxycholic acid injections reduce small fat deposits over multiple sessions.",
  },
  {
    label: "Results Duration",
    neckLift: "8 to 15 years.",
    nonSurgical: "6 to 18 months depending on treatment.",
  },
  {
    label: "Appropriate For",
    neckLift: "Moderate to significant neck aging, platysmal banding, excess skin.",
    nonSurgical: "Early aging, maintenance between surgical procedures, patients not ready for surgery.",
  },
];

function VsNonSurgicalSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="neck-vs-nonsurgical-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Understanding Your Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">
            Neck Lift vs. Non-Surgical Neck Treatments
          </h2>
        </div>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <div className="hidden lg:grid lg:grid-cols-3 gap-0 border border-[#E8E2D9] bg-[#E8E2D9] min-w-[640px]">
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 font-medium" />
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 text-center leading-tight">Neck lift</div>
            <div className="bg-premium-purple text-warm-white font-serif text-sm p-3 text-center leading-tight">Non-surgical</div>
            {VS_NONSURGICAL_ROWS.map((row) => (
              <React.Fragment key={row.label}>
                <div className="bg-white p-3 text-sm font-medium text-deep-navy border-t border-[#E8E2D9]">{row.label}</div>
                <div className="bg-white p-3 text-sm text-deep-navy/70 leading-relaxed border-t border-[#E8E2D9]">{row.neckLift}</div>
                <div className="bg-white p-3 text-sm text-deep-navy/70 leading-relaxed border-t border-[#E8E2D9]">{row.nonSurgical}</div>
              </React.Fragment>
            ))}
          </div>
          <div className="hidden md:grid lg:hidden md:grid-cols-2 gap-4">
            {VS_NONSURGICAL_ROWS.map((row) => (
              <div key={row.label} className="bg-white p-6 border border-[#E8E2D9] md:col-span-2">
                <h3 className="font-serif text-lg text-premium-purple mb-4">{row.label}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Neck lift</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.neckLift}</p>
                  </div>
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Non-surgical</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.nonSurgical}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="md:hidden space-y-4">
            {VS_NONSURGICAL_ROWS.map((row) => (
              <div key={row.label} className="bg-white p-6 border border-[#E8E2D9]">
                <h3 className="font-serif text-lg text-premium-purple mb-4">{row.label}</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Neck lift</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.neckLift}</p>
                  </div>
                  <div>
                    <p className="font-medium text-deep-navy mb-1">Non-surgical</p>
                    <p className="text-deep-navy/70 leading-relaxed">{row.nonSurgical}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-deep-navy/60 text-center mt-8 max-w-3xl mx-auto leading-relaxed">
            Non-surgical treatments address early aging effectively and are appropriate for the right patient. When skin
            laxity, platysmal banding, and fat accumulation are all present simultaneously, surgery is the only treatment that
            corrects all three in a single procedure with lasting results.
          </p>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Week 1",
      description:
        "Swelling, bruising, and tightness are expected. The compression garment stays on continuously. Head elevation reduces swelling. Rest is the priority.",
      icon: BedDouble,
    },
    {
      period: "Week 2",
      description:
        "Compression garment continues, worn during the day. Most patients resume light desk activity. Bruising fades from dark to yellow. Sutures are removed by day 7 to 10.",
      icon: Monitor,
    },
    {
      period: "Weeks 3 to 4",
      description:
        "Most patients return to normal routine. Strenuous activity is still avoided. The neck looks significantly improved even through residual swelling.",
      icon: Dumbbell,
    },
    {
      period: "Months 2 to 3",
      description:
        "Swelling resolves fully. Final jawline and neck contour is visible. Results continue to refine over 6 months as deeper tissue layers settle.",
      icon: Layers,
    },
  ];

  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">
            Who Is a Good Candidate for Neck Lift Surgery?
          </h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Most neck lift patients are between 35 and 65, though age alone does not determine candidacy. The degree of
              structural neck aging does. A patient in their late 30s with significant platysmal banding, poor skin
              elasticity from weight fluctuations, and submental fat accumulation is a better candidate than a 55-year-old
              with mild laxity and good skin quality. The structural concern, not the number, determines the appropriate
              intervention.
            </p>
            <p>
              Good candidates are in good general health, non-smokers or willing to stop smoking at least 4 weeks before
              surgery, and at a stable weight. Significant weight loss after a neck lift stretches the corrected skin and
              undermines the result. Patients planning major weight changes should complete that process before surgery.
              Patients with realistic expectations about what surgery corrects and what natural aging continues to do after
              surgery get the most consistent satisfaction from the outcome.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Neck" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Questions</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Frequently Asked Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {NECK_LIFT_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Neck Lift Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your neck skin laxity, platysma muscle condition, submental fat, and
          jawline structure to determine which neck lift technique addresses your specific anatomy. You leave with a clear
          surgical plan, honest outcome expectations, and a full understanding of what recovery involves.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Facelift",
      description: "Address lower face jowling and laxity alongside neck correction for complete rejuvenation.",
      href: "/facelift-surgery-mumbai",
    },
    {
      name: "Chin Augmentation",
      description: "Strengthen jaw projection to complement a defined, lifted neck contour.",
      href: "/chin-augmentation-in-mumbai",
    },
    {
      name: "Thread Lift",
      description: "Non-surgical lifting option for patients with early neck laxity not yet requiring surgery.",
      href: "/thread-lift-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-10 md:py-14 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Related Procedures</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Related Procedures</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function NeckLiftPage() {
  return (
    <>
      <SEO
        title="Neck Lift Surgery | Dr. Vicky Jain"
        description="Sagging neck skin, double chin, and neck bands need structural correction. Dr. Vicky Jain performs neck lift surgery with lasting results. Book today."
        canonical="/neck-lift-surgery-mumbai"
      />
      <NeckLiftJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhatCorrectsSection />
      <ProcedureTypesSection />
      <HowSurgeryPerformedSection />
      <VsNonSurgicalSection />
      <RecoveryTimelineSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
